import { get } from './API';

/**
 * The AI decision log — one append-only row per thing an AI function did.
 *
 * READ-ONLY. There is no write call here and there should not be: a decision
 * is recorded by whichever service invoked the model, and the screen over it
 * observes. The only endpoint is `GET /ai/decisions`.
 *
 * ── THE RESPONSE CARRIES THE FILTER'S OWN VOCABULARY ──
 * `{ data, pagination, functions }`. `functions` is the code-owned registry
 * (`AI_FUNCTIONS`), shipped with every page of rows so the Function filter is
 * driven by WHAT EXISTS rather than by what happens to have run. Nine of the
 * ten functions record nothing today — derive the filter from the rows and
 * nine of them would be unselectable, and "this function has never logged
 * anything" is the single most useful thing this screen can say. So the list
 * is never built from the data, and never hardcoded here either.
 *
 * NO FILTER IS REQUIRED. With none at all the endpoint lists every decision
 * ever recorded, newest first, which is what the screen opens on.
 */

/**
 * Where a decision got to, as the screen says it.
 *
 * `AI_DECISION_STATES` on the server has SIX members and this mirrors all
 * six, in its order. Three of the words carry the screen's vocabulary —
 * Advised, Awaiting review, Overridden — and the rest are states that exist in
 * the enum and so have to be nameable: a row cannot render blank.
 *
 * AN EARLIER VERSION OF THIS LIST SAID FIVE AND HELD FIVE, and `no_action` was
 * the one it was missing — so every row of it rendered the raw enum string
 * through the fallback. It is not rare: it is what the agent's own "no reply
 * needed" conclusion records (`DRAFT_RESOLUTION_STATES`), which is a judgement
 * the inbound path reaches on real customer mail, and what a person resolving
 * a thread that way appends. Counted from the server's enum rather than from
 * this comment next time.
 *
 * `tone` is the status dot's colour, and the rule it encodes is the one the
 * design states outright: AMBER MEANS A HUMAN IS NEEDED, NEVER THAT
 * SOMETHING CRASHED. So `awaiting_review` is the only amber of the six and
 * `failed` is red.
 *
 * `no_action` IS GOOD NEWS AND MUST NOT READ AS A FAILURE. "It ran, it worked,
 * nothing was needed" is the right outcome for a newsletter or a thank-you —
 * the same reason `no_reply_needed` is a success on the desk — and a screen
 * that drew it red would make a correct decision look like a broken one.
 *
 * `auto_applied` keeps its own word rather than collapsing into Advised.
 * "It was applied with nobody looking" is not advice, and hiding that
 * distinction would hide the exact thing an observability screen exists to
 * surface.
 */
export const DECISION_STATES = [
  { value: 'proposed', label: 'Advised', tone: 'good' },
  { value: 'auto_applied', label: 'Auto-applied', tone: 'good' },
  { value: 'awaiting_review', label: 'Awaiting review', tone: 'warn' },
  { value: 'overridden', label: 'Overridden', tone: 'changed' },
  { value: 'failed', label: 'Failed', tone: 'bad' },
  { value: 'no_action', label: 'No action needed', tone: 'good' },
];

/** The state the "Needs review" pill filters to. The queue wanting a human. */
export const NEEDS_REVIEW_STATE = 'awaiting_review';

/**
 * Whether the RUN worked, independently of what it decided.
 *
 * Not the same question as `state`, and the reason this column exists at all:
 * a tool denied by a permission, or degraded onto less context than it wanted,
 * returns a polite answer and raises nothing — the Lambda's error count stays
 * at zero while the function is quietly failing. So a denial and a degradation
 * are shown as prominently as a break, which is what they are operationally.
 */
export const TOOL_OUTCOMES = [
  { value: 'ok', label: 'Ran', tone: 'good' },
  { value: 'denied', label: 'Denied', tone: 'bad' },
  { value: 'degraded', label: 'Degraded', tone: 'bad' },
  { value: 'error', label: 'Error', tone: 'bad' },
];

/** A descriptor by value, so an enum member the server adds still renders. */
const descriptor = (list, value) => (
  list.find((entry) => entry.value === value)
  || (value ? { value, label: value, tone: 'muted' } : null)
);

export const stateDescriptor = (value) => descriptor(DECISION_STATES, value);
export const outcomeDescriptor = (value) => descriptor(TOOL_OUTCOMES, value);

export default {
  /**
   * One page of decisions, plus the registry and the pagination envelope.
   *
   * `filters` takes any of `ai_function`, `state`, `tool_outcome`,
   * `subject_reference`, `from`, `to`, `page`, `limit` (max 200). Empty values
   * are dropped rather than sent as `?state=`, which the server would read as
   * a real filter for the empty string.
   *
   * Returned already unpacked — `{ decisions, pagination, functions }` — so a
   * reset jest mock answering `undefined` renders as an empty list rather than
   * throwing.
   */
  list: async (filters = {}) => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    );
    const response = await get('/ai/decisions', params);
    const body = response?.data || {};
    return {
      decisions: body.data || [],
      pagination: body.pagination || {
        page: 1, limit: Number(params.limit) || 50, total: 0, pages: 0,
      },
      functions: body.functions || [],
    };
  },
};
